import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Filter,
  ChevronRight,
  Plane,
  Clock,
  MapPin,
  X,
  RefreshCw,
  Download,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Radio,
  ListChecks,
  UserPlus,
  User,
  ChevronDown,
  Edit3,
  Save,
  Plus,
} from 'lucide-react';
import dayjs from 'dayjs';
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from '@/services/api';
import { onEvent } from '@/services/socket';
import { useAuth } from '@/hooks/useAuth';
import { hasMinRole } from '@/utils/roles';
import {
  API_ROUTES,
  FLIGHT_STATUS_COLORS,
  TASK_STATUS_COLORS,
  FlightStatus,
  FlightDirection,
  Role,
  type AircraftCategory,
} from '@avgrp/shared';

// ─── Import result type ──────────────────────────────────────

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ flight: string; reason: string }>;
  airportsCreated: string[];
}

interface AmadeusResult {
  updated: number;
  skipped: number;
  notFound: number;
  scraped: number;
  errors: Array<{ flight: string; reason: string }>;
}

interface GenerateTasksResult {
  flightsProcessed: number;
  totalTasksGenerated: number;
}

interface AvailableEmployee {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  qualifications: {
    qualification: { id: string; name: string; code: string };
  }[];
}

// ─── Types (matching API response shape) ─────────────────────

interface Flight {
  id: string;
  flightNumber: string;
  direction: FlightDirection;
  status: FlightStatus;
  std: string;
  sta: string;
  etd: string | null;
  eta: string | null;
  gate: string | null;
  stand: string | null;
  isVip: boolean;
  paxCount: number | null;
  prepTime: number;
  postTime: number;
  aircraft: {
    id: string;
    registration: string;
    aircraftType: {
      id: string;
      name: string;
      iataCode: string;
      category: AircraftCategory;
    };
  };
  origin: {
    id: string;
    iataCode: string;
    name: string;
  };
  destination: {
    id: string;
    iataCode: string;
    name: string;
  };
  base: {
    id: string;
    name: string;
  };
  flightTasks: {
    id: string;
    name: string;
    phase: string;
    status: string;
    plannedStart: string;
    plannedEnd: string;
    priority: number;
    taskTemplate?: { id: string; code: string; name: string } | null;
    assignments: {
      id: string;
      resourceType: string;
      status: string;
      user?: { id: string; firstName: string; lastName: string; employeeId: string } | null;
    }[];
  }[];
}

// ─── Component ──────────────────────────────────────────────

export function FlightsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [directionFilter, setDirectionFilter] = useState<string>('');
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [amadeusResult, setAmadeusResult] = useState<AmadeusResult | null>(null);
  const [amadeusProgress, setAmadeusProgress] = useState<{ current: number; total: number; phase: string } | null>(null);
  const [generateResult, setGenerateResult] = useState<GenerateTasksResult | null>(null);
  const [assigningTaskId, setAssigningTaskId] = useState<string | null>(null);
  const [editingRegistration, setEditingRegistration] = useState(false);
  const [registrationDraft, setRegistrationDraft] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    flightNumber: '',
    direction: FlightDirection.DEPARTURE,
    date: selectedDate,
    stdTime: '',
    staTime: '',
    aircraftId: '',
    gate: '',
    stand: '',
  });

  // ─── Amadeus progress listener (WebSocket) ─────────────────
  useEffect(() => {
    const unsub = onEvent<{ current: number; total: number; flightNumber: string; found: boolean; phase: string }>(
      'amadeus:progress',
      (data) => {
        if (data.phase === 'done') {
          setAmadeusProgress(null);
        } else {
          setAmadeusProgress({ current: data.current, total: data.total, phase: data.phase });
        }
      }
    );
    return unsub;
  }, []);

  // ─── Import Live mutation ─────────────────────────────────
  const importMutation = useMutation({
    mutationFn: (date: string) =>
      apiPost<{ data: ImportResult }>(`${API_ROUTES.FLIGHTS}/import-live`, { date }),
    onSuccess: (res) => {
      setImportResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      setTimeout(() => setImportResult(null), 15000);
    },
  });

  // ─── Clear flights mutation ─────────────────────────────────
  const clearMutation = useMutation({
    mutationFn: (date: string) =>
      apiPost<{ data: { deleted: number; tasksDeleted: number; assignmentsDeleted: number } }>(
        `${API_ROUTES.FLIGHTS}/clear`,
        { date }
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      setImportResult({
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [],
        airportsCreated: [],
      });
      setClearCount(res.data.deleted);
      setTimeout(() => { setImportResult(null); setClearCount(null); }, 10000);
    },
  });
  const [clearCount, setClearCount] = useState<number | null>(null);

  // ─── Amadeus live update mutation ────────────────────────────
  const amadeusMutation = useMutation({
    mutationFn: (date: string) => {
      setAmadeusProgress(null); // Reset progress
      return apiPost<{ data: AmadeusResult }>(`${API_ROUTES.FLIGHTS}/update-amadeus`, { date }, { timeout: 600_000 });
    },
    onSuccess: (res) => {
      setAmadeusProgress(null);
      setAmadeusResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      setTimeout(() => setAmadeusResult(null), 15000);
    },
    onError: () => {
      setAmadeusProgress(null);
    },
  });

  // ─── Status change mutation ─────────────────────────────────
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FlightStatus }) =>
      apiPatch<{ data: Flight }>(`${API_ROUTES.FLIGHTS}/${id}/status`, { status }),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      // Update the selected flight in place so the panel reflects the change
      if (selectedFlight && variables.id === selectedFlight.id) {
        setSelectedFlight({ ...selectedFlight, status: variables.status });
      }
    },
  });

  // ─── Update flight mutation (registration, gate, stand, etc.) ──
  const updateFlightMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string; [key: string]: unknown }) =>
      apiPut<{ data: Flight }>(`${API_ROUTES.FLIGHTS}/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      setEditingRegistration(false);
    },
  });

  // ─── Delete single flight mutation ─────────────────────────────
  const deleteFlightMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`${API_ROUTES.FLIGHTS}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      if (selectedFlight) setSelectedFlight(null);
    },
  });

  // ─── Create flight mutation ─────────────────────────────────
  const createFlightMutation = useMutation({
    mutationFn: (data: {
      flightNumber: string;
      direction: FlightDirection;
      date: string;
      std: string;
      sta: string;
      aircraftId: string;
      gate?: string;
      stand?: string;
      baseId: string;
    }) => apiPost<{ data: Flight }>(API_ROUTES.FLIGHTS, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      setShowCreateForm(false);
      setCreateForm({
        flightNumber: '',
        direction: FlightDirection.DEPARTURE,
        date: selectedDate,
        stdTime: '',
        staTime: '',
        aircraftId: '',
        gate: '',
        stand: '',
      });
    },
  });

  // ─── Generate tasks mutation ─────────────────────────────────
  const generateTasksMutation = useMutation({
    mutationFn: () =>
      apiPost<{ data: unknown[]; summary: GenerateTasksResult }>(
        `${API_ROUTES.TASKS}/generate-batch`,
        { baseId: user?.baseId, date: selectedDate }
      ),
    onSuccess: (res) => {
      setGenerateResult(res.summary);
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      setTimeout(() => setGenerateResult(null), 10000);
    },
  });

  // ─── Assign resource mutation ─────────────────────────────────
  const assignMutation = useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
      apiPost(`${API_ROUTES.TASKS}/${taskId}/assign`, {
        flightTaskId: taskId,
        resourceType: 'HUMAN',
        userId,
      }),
    onSuccess: () => {
      setAssigningTaskId(null);
      queryClient.invalidateQueries({ queryKey: ['flights'] });
    },
  });

  // ─── Available employees query ─────────────────────────────────
  const { data: employees } = useQuery({
    queryKey: ['available-employees', user?.baseId, selectedDate],
    queryFn: () =>
      apiGet<{ data: AvailableEmployee[] }>(`${API_ROUTES.RESOURCES}/employees`, {
        params: { baseId: user?.baseId, date: selectedDate },
      }),
    select: (res) => res.data,
    enabled: !!user?.baseId && !!assigningTaskId,
  });

  // ─── Aircraft list for create form ─────────────────────────────
  const { data: aircraftList } = useQuery({
    queryKey: ['aircraft'],
    queryFn: () =>
      apiGet<{ data: { id: string; registration: string; aircraftType: { name: string } }[] }>(
        `${API_ROUTES.RESOURCES}/aircraft`
      ),
    select: (res) => res.data,
    enabled: showCreateForm,
  });

  const { data: flights, isLoading, refetch } = useQuery({
    queryKey: ['flights', user?.baseId, selectedDate, statusFilter, directionFilter],
    queryFn: () =>
      apiGet<{ data: Flight[]; count: number }>(API_ROUTES.FLIGHTS, {
        params: {
          baseId: user?.baseId,
          date: selectedDate,
          status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
          direction: directionFilter || undefined,
        },
      }),
    select: (res) => res.data,
    enabled: !!user?.baseId,
  });

  const flightList = flights ?? [];

  // Keep selectedFlight in sync with query data (e.g. after task generation or assignment)
  useEffect(() => {
    if (selectedFlight && flightList.length > 0) {
      const updated = flightList.find((f) => f.id === selectedFlight.id);
      if (updated && updated !== selectedFlight) {
        setSelectedFlight(updated);
      }
    }
  }, [flightList]);

  const filteredFlights = flightList.filter((f) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        f.flightNumber.toLowerCase().includes(q) ||
        f.aircraft?.registration?.toLowerCase().includes(q) ||
        f.aircraft?.aircraftType?.name?.toLowerCase().includes(q) ||
        f.destination.iataCode.toLowerCase().includes(q) ||
        f.destination.name.toLowerCase().includes(q) ||
        f.origin.iataCode.toLowerCase().includes(q) ||
        f.origin.name.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flights</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and track all flight operations
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasMinRole(user?.role, Role.MANAGER) && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="btn-primary bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Flight
            </button>
          )}
          {hasMinRole(user?.role, Role.MANAGER) && (
            <button
              onClick={() => {
                if (window.confirm(`Clear all flights for ${selectedDate}? This cannot be undone.`)) {
                  clearMutation.mutate(selectedDate);
                }
              }}
              disabled={clearMutation.isPending}
              className="btn-secondary text-red-600 hover:bg-red-50 hover:border-red-300"
            >
              {clearMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {clearMutation.isPending ? 'Clearing...' : 'Clear Day'}
            </button>
          )}
          {hasMinRole(user?.role, Role.MANAGER) && (
            <button
              onClick={() => generateTasksMutation.mutate()}
              disabled={generateTasksMutation.isPending}
              className="btn-secondary border-aviation-300 text-aviation-700 hover:bg-aviation-50"
            >
              {generateTasksMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ListChecks className="mr-2 h-4 w-4" />
              )}
              {generateTasksMutation.isPending ? 'Generating...' : 'Generate Tasks'}
            </button>
          )}
          {hasMinRole(user?.role, Role.MANAGER) && (
            <button
              onClick={() => importMutation.mutate(selectedDate)}
              disabled={importMutation.isPending}
              className="btn-primary"
            >
              {importMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {importMutation.isPending ? 'Importing...' : 'Import Live'}
            </button>
          )}
          {hasMinRole(user?.role, Role.MANAGER) && (
            <button
              onClick={() => amadeusMutation.mutate(selectedDate)}
              disabled={amadeusMutation.isPending}
              className="btn-secondary border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {amadeusMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Radio className="mr-2 h-4 w-4" />
              )}
              {amadeusMutation.isPending
                ? amadeusProgress
                  ? amadeusProgress.phase === 'login'
                    ? 'Logging into Amadeus...'
                    : `Amadeus ${amadeusProgress.current}/${amadeusProgress.total}`
                  : 'Updating from Amadeus...'
                : 'Update from Amadeus'}
            </button>
          )}
          <button onClick={() => refetch()} className="btn-secondary">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Import / Clear result feedback */}
      {importResult && (
        <div className="flex items-start justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
            <div className="text-sm text-emerald-800">
              <p className="font-medium">
                {clearCount != null ? 'Flights cleared' : 'Import complete'}
              </p>
              <p className="mt-0.5">
                {clearCount != null ? (
                  <>{clearCount} flight{clearCount !== 1 ? 's' : ''} deleted</>
                ) : (
                  <>
                    {importResult.created} created, {importResult.updated} updated
                    {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
                    {importResult.airportsCreated.length > 0 && (
                      <span>
                        {' '}· New airports: {importResult.airportsCreated.join(', ')}
                      </span>
                    )}
                  </>
                )}
              </p>
              {importResult.errors.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-emerald-600 hover:text-emerald-800">
                    {importResult.errors.length} error{importResult.errors.length > 1 ? 's' : ''}
                  </summary>
                  <ul className="mt-1 space-y-0.5 text-xs text-emerald-700">
                    {importResult.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>{e.flight}: {e.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
          <button
            onClick={() => setImportResult(null)}
            className="ml-3 rounded p-0.5 text-emerald-400 hover:text-emerald-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Amadeus update result feedback */}
      {amadeusResult && (
        <div className="flex items-start justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Amadeus update complete</p>
              <p className="mt-0.5">
                {amadeusResult.scraped} scraped from Amadeus, {amadeusResult.updated} updated
                {amadeusResult.notFound > 0 && `, ${amadeusResult.notFound} not found in DB`}
                {amadeusResult.skipped > 0 && `, ${amadeusResult.skipped} skipped`}
              </p>
              {amadeusResult.errors.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-amber-600 hover:text-amber-800">
                    {amadeusResult.errors.length} error{amadeusResult.errors.length > 1 ? 's' : ''}
                  </summary>
                  <ul className="mt-1 space-y-0.5 text-xs text-amber-700">
                    {amadeusResult.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>{e.flight}: {e.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
          <button
            onClick={() => setAmadeusResult(null)}
            className="ml-3 rounded p-0.5 text-amber-400 hover:text-amber-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {amadeusMutation.isError && !amadeusResult && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Amadeus update failed: {(amadeusMutation.error as any)?.response?.data?.error?.message
            || (amadeusMutation.error as any)?.message
            || 'Unknown error'}
        </div>
      )}

      {/* Generate tasks result feedback */}
      {generateResult && (
        <div className="flex items-start justify-between rounded-lg border border-aviation-200 bg-aviation-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-aviation-600" />
            <div className="text-sm text-aviation-800">
              <p className="font-medium">Tasks generated</p>
              <p className="mt-0.5">
                {generateResult.flightsProcessed} flights processed, {generateResult.totalTasksGenerated} tasks created
              </p>
            </div>
          </div>
          <button
            onClick={() => setGenerateResult(null)}
            className="ml-3 rounded p-0.5 text-aviation-400 hover:text-aviation-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {importMutation.isError && !importResult && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Import failed: {(importMutation.error as any)?.response?.data?.error?.message
            || (importMutation.error as any)?.message
            || 'Unknown error'}
        </div>
      )}

      {/* ─── Create Flight Form ─────────────────────────────── */}
      {showCreateForm && hasMinRole(user?.role, Role.MANAGER) && (
        <div className="card">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Create Flight</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!user?.baseId) return;
              const std = `${createForm.date}T${createForm.stdTime}:00.000Z`;
              const sta = `${createForm.date}T${createForm.staTime}:00.000Z`;
              createFlightMutation.mutate({
                flightNumber: createForm.flightNumber,
                direction: createForm.direction,
                date: createForm.date,
                std,
                sta,
                aircraftId: createForm.aircraftId,
                gate: createForm.gate || undefined,
                stand: createForm.stand || undefined,
                baseId: user.baseId,
              });
            }}
            className="grid grid-cols-2 gap-4 sm:grid-cols-4"
          >
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Flight Number *</label>
              <input
                type="text"
                required
                value={createForm.flightNumber}
                onChange={(e) => setCreateForm({ ...createForm, flightNumber: e.target.value.toUpperCase() })}
                placeholder="e.g. EK801"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Direction *</label>
              <select
                required
                value={createForm.direction}
                onChange={(e) => setCreateForm({ ...createForm, direction: e.target.value as FlightDirection })}
                className="input-field w-full"
              >
                <option value="DEPARTURE">Departure</option>
                <option value="ARRIVAL">Arrival</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Date *</label>
              <input
                type="date"
                required
                value={createForm.date}
                onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">STD (Departure) *</label>
              <input
                type="time"
                required
                value={createForm.stdTime}
                onChange={(e) => setCreateForm({ ...createForm, stdTime: e.target.value })}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">STA (Arrival) *</label>
              <input
                type="time"
                required
                value={createForm.staTime}
                onChange={(e) => setCreateForm({ ...createForm, staTime: e.target.value })}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Aircraft *</label>
              <select
                required
                value={createForm.aircraftId}
                onChange={(e) => setCreateForm({ ...createForm, aircraftId: e.target.value })}
                className="input-field w-full"
              >
                <option value="">Select aircraft</option>
                {(aircraftList ?? []).map((ac) => (
                  <option key={ac.id} value={ac.id}>
                    {ac.registration} ({ac.aircraftType.name})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Gate</label>
              <input
                type="text"
                value={createForm.gate}
                onChange={(e) => setCreateForm({ ...createForm, gate: e.target.value })}
                placeholder="Optional"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Stand</label>
              <input
                type="text"
                value={createForm.stand}
                onChange={(e) => setCreateForm({ ...createForm, stand: e.target.value })}
                placeholder="Optional"
                className="input-field w-full"
              />
            </div>
            <div className="col-span-full flex items-center gap-2">
              <button
                type="submit"
                disabled={createFlightMutation.isPending}
                className="btn-primary"
              >
                {createFlightMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {createFlightMutation.isPending ? 'Creating...' : 'Create Flight'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              {createFlightMutation.isError && (
                <span className="text-sm text-red-600">
                  <AlertCircle className="mr-1 inline h-4 w-4" />
                  {(createFlightMutation.error as any)?.response?.data?.error?.message
                    || (createFlightMutation.error as any)?.message
                    || 'Failed to create flight'}
                </span>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Filters bar */}
      <div className="card flex flex-wrap items-center gap-4">
        {/* Date picker */}
        <div>
          <label htmlFor="flight-date" className="sr-only">Date</label>
          <input
            id="flight-date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input-field"
          />
        </div>

        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search flights..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>

        {/* Status filter (multi-select chips) */}
        <div className="relative">
          <button
            onClick={() => {
              const el = document.getElementById('status-dropdown');
              if (el) el.classList.toggle('hidden');
            }}
            className="input-field flex w-auto items-center gap-2"
          >
            <Filter className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-sm">
              {statusFilter.length === 0 ? 'All statuses' : `${statusFilter.length} selected`}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          </button>
          <div
            id="status-dropdown"
            className="absolute left-0 top-full z-30 mt-1 hidden w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            {statusFilter.length > 0 && (
              <button
                onClick={() => setStatusFilter([])}
                className="w-full px-3 py-1.5 text-left text-xs text-aviation-600 hover:bg-gray-50"
              >
                Clear all
              </button>
            )}
            {Object.values(FlightStatus).map((s) => {
              const checked = statusFilter.includes(s);
              return (
                <label key={s} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      setStatusFilter(checked ? statusFilter.filter((v) => v !== s) : [...statusFilter, s]);
                    }}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-aviation-600"
                  />
                  <span
                    className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                    style={{
                      backgroundColor: `${FLIGHT_STATUS_COLORS[s] || '#6B7280'}15`,
                      color: FLIGHT_STATUS_COLORS[s] || '#6B7280',
                    }}
                  >
                    {s.replace('_', ' ')}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Direction filter */}
        <select
          value={directionFilter}
          onChange={(e) => setDirectionFilter(e.target.value)}
          className="input-field w-auto"
        >
          <option value="">All directions</option>
          <option value="ARRIVAL">Arrivals</option>
          <option value="DEPARTURE">Departures</option>
        </select>
      </div>

      {/* Flight table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Flight</th>
                  <th className="table-header">Route</th>
                  <th className="table-header">Registration</th>
                  <th className="table-header">Scheduled</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Gate</th>
                  <th className="table-header">Stand</th>
                  <th className="table-header">Tasks</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody>
                {filteredFlights.map((flight) => {
                  const statusColor = FLIGHT_STATUS_COLORS[flight.status] || '#6B7280';
                  const isSelected = selectedFlight?.id === flight.id;
                  const tasksTotal = flight.flightTasks?.length ?? 0;
                  const tasksCompleted = flight.flightTasks?.filter((t) => t.status === 'COMPLETED').length ?? 0;

                  return (
                    <tr
                      key={flight.id}
                      onClick={() => setSelectedFlight(isSelected ? null : flight)}
                      className={`cursor-pointer border-b border-gray-100 transition-colors last:border-0
                        ${isSelected ? 'bg-aviation-50' : 'hover:bg-gray-50'}
                      `}
                    >
                      <td className="table-cell font-mono font-semibold">
                        {flight.flightNumber}
                      </td>
                      <td className="table-cell">
                        <span className="font-mono text-gray-500">{flight.origin.iataCode}</span>
                        <span className="mx-1 text-gray-300">&rarr;</span>
                        <span className="font-mono">{flight.destination.iataCode}</span>
                      </td>
                      <td className="table-cell font-mono text-gray-600">
                        {flight.aircraft?.registration || '-'}
                      </td>
                      <td className="table-cell font-mono">
                        {dayjs(flight.std).format('HH:mm')}
                      </td>
                      <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                        {hasMinRole(user?.role, Role.SUPERVISOR) ? (
                          <select
                            value={flight.status}
                            onChange={(e) =>
                              statusMutation.mutate({
                                id: flight.id,
                                status: e.target.value as FlightStatus,
                              })
                            }
                            className="cursor-pointer rounded-full border-0 bg-transparent px-2 py-0.5 text-xs font-semibold focus:ring-1"
                            style={{
                              backgroundColor: `${statusColor}15`,
                              color: statusColor,
                            }}
                          >
                            {Object.values(FlightStatus).map((s) => (
                              <option key={s} value={s}>
                                {s.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className="rounded-full px-2 py-0.5 text-xs font-semibold"
                            style={{
                              backgroundColor: `${statusColor}15`,
                              color: statusColor,
                            }}
                          >
                            {flight.status.replace('_', ' ')}
                          </span>
                        )}
                      </td>
                      <td className="table-cell font-mono text-gray-500">{flight.gate || '-'}</td>
                      <td className="table-cell font-mono text-gray-500">{flight.stand || '-'}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
                            <div
                              className="h-full rounded-full bg-emerald-500"
                              style={{
                                width: `${tasksTotal > 0 ? (tasksCompleted / tasksTotal) * 100 : 0}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-400">
                            {tasksCompleted}/{tasksTotal}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {hasMinRole(user?.role, Role.MANAGER) && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to delete flight ${flight.flightNumber}?`)) {
                                  deleteFlightMutation.mutate(flight.id);
                                }
                              }}
                              disabled={deleteFlightMutation.isPending}
                              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Delete flight"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          <ChevronRight
                            onClick={() => setSelectedFlight(isSelected ? null : flight)}
                            className={`h-4 w-4 cursor-pointer transition-transform ${isSelected ? 'rotate-90 text-aviation-600' : 'text-gray-400'}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredFlights.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-sm text-gray-400">
                      No flights found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Flight detail slide-over panel ─────────────────────── */}
      {selectedFlight && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30 bg-black/20 transition-opacity"
            onClick={() => { setSelectedFlight(null); setAssigningTaskId(null); }}
          />

          {/* Panel */}
          <div className="fixed right-0 top-0 z-40 flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl">
            {/* Panel header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg"
                  style={{
                    backgroundColor: `${selectedFlight.direction === 'DEPARTURE' ? '#3B82F6' : '#10B981'}15`,
                  }}
                >
                  <Plane
                    className="h-5 w-5"
                    style={{
                      color: selectedFlight.direction === 'DEPARTURE' ? '#3B82F6' : '#10B981',
                      transform: selectedFlight.direction === 'DEPARTURE' ? 'rotate(-45deg)' : 'rotate(45deg)',
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedFlight.flightNumber}
                  </h3>
                  <p className="text-xs text-gray-400">
                    {selectedFlight.origin.iataCode} &rarr; {selectedFlight.destination.iataCode}
                    {' · '}
                    {selectedFlight.direction === 'DEPARTURE' ? 'Departure' : 'Arrival'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setSelectedFlight(null); setAssigningTaskId(null); }}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Panel body — scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-5">
                {/* Status — editable dropdown (SUPERVISOR+) or read-only badge */}
                <div>
                  <label className="mb-1 block text-xs text-gray-400">Status</label>
                  {hasMinRole(user?.role, Role.SUPERVISOR) ? (
                    <select
                      value={selectedFlight.status}
                      onChange={(e) =>
                        statusMutation.mutate({
                          id: selectedFlight.id,
                          status: e.target.value as FlightStatus,
                        })
                      }
                      disabled={statusMutation.isPending}
                      className="input-field w-full font-medium"
                      style={{
                        borderColor: FLIGHT_STATUS_COLORS[selectedFlight.status],
                        color: FLIGHT_STATUS_COLORS[selectedFlight.status],
                      }}
                    >
                      {Object.values(FlightStatus).map((s) => (
                        <option key={s} value={s} style={{ color: FLIGHT_STATUS_COLORS[s] }}>
                          {s.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className="inline-block rounded-full px-2.5 py-1 text-sm font-medium"
                      style={{
                        backgroundColor: `${FLIGHT_STATUS_COLORS[selectedFlight.status]}15`,
                        color: FLIGHT_STATUS_COLORS[selectedFlight.status],
                      }}
                    >
                      {selectedFlight.status.replace('_', ' ')}
                    </span>
                  )}
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Editable Registration */}
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="mb-0.5 flex items-center gap-1 text-[10px] text-gray-400">
                      <Plane className="h-3 w-3" />
                      Registration
                    </div>
                    {hasMinRole(user?.role, Role.MANAGER) && editingRegistration ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={registrationDraft}
                          onChange={(e) => setRegistrationDraft(e.target.value.toUpperCase())}
                          className="w-full rounded border border-gray-300 px-1.5 py-0.5 font-mono text-sm font-semibold focus:border-aviation-500 focus:outline-none"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updateFlightMutation.mutate({ id: selectedFlight.id, registration: registrationDraft });
                            } else if (e.key === 'Escape') {
                              setEditingRegistration(false);
                            }
                          }}
                        />
                        <button
                          onClick={() => updateFlightMutation.mutate({ id: selectedFlight.id, registration: registrationDraft })}
                          className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm font-semibold text-gray-900">
                          {selectedFlight.aircraft?.registration || '-'}
                        </span>
                        {hasMinRole(user?.role, Role.MANAGER) && (
                          <button
                            onClick={() => {
                              setRegistrationDraft(selectedFlight.aircraft?.registration || '');
                              setEditingRegistration(true);
                            }}
                            className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                          >
                            <Edit3 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <DetailItem icon={Plane} label="Aircraft" value={selectedFlight.aircraft?.aircraftType?.name || '-'} />
                  <DetailItem icon={Plane} label="Category" value={selectedFlight.aircraft?.aircraftType?.category || '-'} />
                  <DetailItem
                    icon={Clock}
                    label="Scheduled"
                    value={dayjs(selectedFlight.std).format('HH:mm')}
                  />
                  <DetailItem
                    icon={Clock}
                    label="Estimated"
                    value={
                      selectedFlight.etd
                        ? dayjs(selectedFlight.etd).format('HH:mm')
                        : '--:--'
                    }
                  />
                  <DetailItem icon={MapPin} label="Gate" value={selectedFlight.gate || '-'} />
                  <DetailItem icon={MapPin} label="Stand" value={selectedFlight.stand || '-'} />
                </div>

                {/* Task progress */}
                {(() => {
                  const total = selectedFlight.flightTasks?.length ?? 0;
                  const completed = selectedFlight.flightTasks?.filter((t) => t.status === 'COMPLETED').length ?? 0;
                  return (
                    <div>
                      <div className="mb-2 flex items-center justify-between text-sm">
                        <span className="text-gray-500">Task Progress</span>
                        <span className="font-medium text-gray-700">
                          {completed} / {total}
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-emerald-500 transition-all"
                          style={{
                            width: `${total > 0 ? (completed / total) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* ─── Task list ─────────────────────────────── */}
                {selectedFlight.flightTasks && selectedFlight.flightTasks.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Tasks
                    </h4>
                    <div className="space-y-2">
                      {[...selectedFlight.flightTasks]
                        .sort((a, b) => new Date(a.plannedStart).getTime() - new Date(b.plannedStart).getTime())
                        .map((task) => {
                          const statusColor = TASK_STATUS_COLORS[task.status] || '#6B7280';
                          const activeAssignments = task.assignments?.filter(
                            (a) => a.status !== 'CANCELLED' && a.status !== 'REASSIGNED'
                          ) ?? [];
                          const phaseColors: Record<string, string> = {
                            PREPARATION: '#3B82F6',
                            TURNAROUND: '#8B5CF6',
                            ARRIVAL: '#10B981',
                          };
                          const phaseColor = phaseColors[task.phase] || '#6B7280';

                          return (
                            <div
                              key={task.id}
                              className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                            >
                              {/* Task header */}
                              <div className="flex items-center justify-between gap-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="truncate text-sm font-semibold text-gray-800">
                                    {task.taskTemplate?.code || task.name}
                                  </span>
                                  <span
                                    className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase"
                                    style={{
                                      backgroundColor: `${phaseColor}15`,
                                      color: phaseColor,
                                    }}
                                  >
                                    {task.phase.slice(0, 4)}
                                  </span>
                                </div>
                                <span
                                  className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
                                  style={{
                                    backgroundColor: `${statusColor}15`,
                                    color: statusColor,
                                  }}
                                >
                                  {task.status.replace('_', ' ')}
                                </span>
                              </div>

                              {/* Time range */}
                              <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-400">
                                <Clock className="h-3 w-3" />
                                <span className="font-mono">
                                  {dayjs(task.plannedStart).format('HH:mm')} – {dayjs(task.plannedEnd).format('HH:mm')}
                                </span>
                              </div>

                              {/* Assigned employees */}
                              <div className="mt-2 flex flex-wrap items-center gap-1">
                                {activeAssignments.length > 0 ? (
                                  activeAssignments.map((a) => (
                                    <span
                                      key={a.id}
                                      className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
                                    >
                                      <User className="h-2.5 w-2.5" />
                                      {a.user ? `${a.user.firstName} ${a.user.lastName[0]}.` : 'Material'}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-[10px] italic text-gray-300">
                                    Unassigned
                                  </span>
                                )}

                                {/* Assign button (MANAGER+) */}
                                {hasMinRole(user?.role, Role.MANAGER) && task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
                                  <div className="relative">
                                    <button
                                      onClick={() =>
                                        setAssigningTaskId(
                                          assigningTaskId === task.id ? null : task.id
                                        )
                                      }
                                      className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:bg-aviation-100 hover:text-aviation-700 transition-colors"
                                    >
                                      <UserPlus className="h-2.5 w-2.5" />
                                      Assign
                                    </button>

                                    {/* Employee picker dropdown */}
                                    {assigningTaskId === task.id && (
                                      <>
                                        <div
                                          className="fixed inset-0 z-40"
                                          onClick={() => setAssigningTaskId(null)}
                                        />
                                        <div className="absolute right-0 top-full z-50 mt-1 w-64 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                                          {!employees ? (
                                            <div className="flex items-center justify-center py-3">
                                              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                            </div>
                                          ) : (employees as AvailableEmployee[]).length === 0 ? (
                                            <p className="px-3 py-2 text-xs text-gray-400">
                                              No employees available
                                            </p>
                                          ) : (
                                            (employees as AvailableEmployee[]).map((emp) => {
                                              const alreadyAssigned = activeAssignments.some(
                                                (a) => a.user?.id === emp.id
                                              );
                                              return (
                                                <button
                                                  key={emp.id}
                                                  disabled={alreadyAssigned || assignMutation.isPending}
                                                  onClick={() =>
                                                    assignMutation.mutate({
                                                      taskId: task.id,
                                                      userId: emp.id,
                                                    })
                                                  }
                                                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ${
                                                    alreadyAssigned
                                                      ? 'cursor-not-allowed bg-gray-50 text-gray-300'
                                                      : 'hover:bg-aviation-50'
                                                  }`}
                                                >
                                                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-aviation-100 text-[10px] font-bold text-aviation-700">
                                                    {emp.firstName[0]}{emp.lastName[0]}
                                                  </div>
                                                  <div className="min-w-0 flex-1">
                                                    <span className="font-medium text-gray-700">
                                                      {emp.firstName} {emp.lastName}
                                                    </span>
                                                    {emp.qualifications?.length > 0 && (
                                                      <div className="flex flex-wrap gap-0.5 mt-0.5">
                                                        {emp.qualifications.slice(0, 3).map((q, i) => (
                                                          <span key={i} className="rounded bg-gray-100 px-1 py-0 text-[8px] text-gray-500">
                                                            {q.qualification.code}
                                                          </span>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                  {alreadyAssigned && (
                                                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
                                                  )}
                                                </button>
                                              );
                                            })
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* No tasks message */}
                {(!selectedFlight.flightTasks || selectedFlight.flightTasks.length === 0) && (
                  <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center">
                    <ListChecks className="mx-auto mb-2 h-6 w-6 text-gray-300" />
                    <p className="text-xs text-gray-400">
                      No tasks generated yet
                    </p>
                    {hasMinRole(user?.role, Role.MANAGER) && (
                      <button
                        onClick={() => generateTasksMutation.mutate()}
                        disabled={generateTasksMutation.isPending}
                        className="mt-2 text-xs font-medium text-aviation-600 hover:text-aviation-800"
                      >
                        Generate tasks for all flights
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Detail Item helper ─────────────────────────────────────

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs text-gray-400">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className="text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}
